'use client';

import React, { useState } from 'react';
import AppLogo from '@/components/ui/AppLogo';

type Role = 'guest' | 'host';
type LoginMethod = 'email' | 'phone';

export default function LoginPage() {
  const [role, setRole] = useState<Role>('guest');
  const [loginMethod, setLoginMethod] = useState<LoginMethod>('email');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const errs: Record<string, string> = {};
    if (loginMethod === 'email') {
      if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) errs.identifier = 'Enter a valid email address';
    } else {
      if (!phone.trim() || !/^\+?[\d\s\-()]{7,}$/.test(phone)) errs.identifier = 'Enter a valid phone number';
    }
    if (!password.trim() || password.length < 6) errs.password = 'Password must be at least 6 characters';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      window.location.href = role === 'host' ? '/host-dashboard' : '/guest-dashboard';
    }, 1200);
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 py-12 overflow-hidden" style={{ background: '#0A1628' }}>
      {/* Ambient background blobs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="blob absolute w-[500px] h-[500px] opacity-20 -top-32 -left-32"
          style={{ background: 'radial-gradient(circle, #4088C6 0%, transparent 70%)' }}
        />
        <div
          className="blob-2 absolute w-[400px] h-[400px] opacity-15 -bottom-24 -right-24"
          style={{ background: 'radial-gradient(circle, #F5A623 0%, transparent 70%)' }}
        />
        <div
          className="absolute w-[300px] h-[300px] opacity-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          style={{ background: 'radial-gradient(circle, #5BA3D9 0%, transparent 70%)' }}
        />
        {/* Grid overlay */}
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'linear-gradient(rgba(64,136,198,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(64,136,198,0.5) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <a href="/home" className="group">
            <div className="transition-transform duration-300 group-hover:scale-105">
              <AppLogo variant="light" size={130} className="cursor-pointer" />
            </div>
          </a>
        </div>

        {/* Glassmorphism Card */}
        <div
          className="rounded-3xl p-8 relative overflow-hidden"
          style={{
            background: 'rgba(15, 30, 53, 0.75)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: '1px solid rgba(64, 136, 198, 0.25)',
            boxShadow: '0 32px 64px rgba(0,0,0,0.5), 0 0 0 1px rgba(64,136,198,0.1), inset 0 1px 0 rgba(255,255,255,0.05)',
          }}
        >
          {/* Top accent line */}
          <div
            className="absolute top-0 left-0 right-0 h-px"
            style={{ background: 'linear-gradient(90deg, transparent, #4088C6, #F5A623, #4088C6, transparent)' }}
          />

          <h1 className="text-2xl font-bold text-white mb-1">Welcome back</h1>
          <p className="text-sm mb-6" style={{ color: '#94A3B8' }}>Sign in to continue your journey across Africa</p>

          {/* Role selector */}
          <div
            className="flex rounded-2xl p-1 mb-5"
            style={{ background: 'rgba(10, 22, 40, 0.6)', border: '1px solid rgba(64,136,198,0.2)' }}
          >
            {(['guest', 'host'] as Role[]).map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setRole(r)}
                className="flex-1 py-2.5 text-sm font-semibold rounded-xl transition-all duration-300 capitalize flex items-center justify-center gap-2"
                style={
                  role === r
                    ? {
                        background: 'linear-gradient(135deg, #4088C6, #5BA3D9)',
                        color: '#fff',
                        boxShadow: '0 4px 16px rgba(64,136,198,0.4)',
                      }
                    : { color: '#94A3B8' }
                }
              >
                <span>{r === 'guest' ? '🧳' : '🏠'}</span>
                {r === 'guest' ? 'Guest' : 'Host'}
              </button>
            ))}
          </div>

          {/* Login method toggle */}
          <div className="flex gap-1 mb-5">
            {(['email', 'phone'] as LoginMethod[]).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => { setLoginMethod(m); setErrors({}); }}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold transition-all duration-200"
                style={
                  loginMethod === m
                    ? { background: 'rgba(245,166,35,0.15)', color: '#F5A623', border: '1px solid rgba(245,166,35,0.35)' }
                    : { background: 'transparent', color: '#94A3B8', border: '1px solid rgba(64,136,198,0.15)' }
                }
              >
                {m === 'email' ? (
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                ) : (
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                )}
                {m === 'email' ? 'Email' : 'Phone'}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email or Phone */}
            <div>
              <label className="block text-sm font-semibold mb-1.5" style={{ color: '#CBD5E1' }}>
                {loginMethod === 'email' ? 'Email address' : 'Phone number'}
              </label>
              {loginMethod === 'email' ? (
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setErrors({}); }}
                  placeholder="you@example.com"
                  className="w-full rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none transition-all duration-200"
                  style={{
                    background: errors.identifier ? 'rgba(239,68,68,0.1)' : 'rgba(10,22,40,0.7)',
                    border: errors.identifier ? '1px solid rgba(239,68,68,0.5)' : '1px solid rgba(64,136,198,0.25)',
                    boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.3)',
                  }}
                  onFocus={(e) => { e.currentTarget.style.border = '1px solid rgba(64,136,198,0.6)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(64,136,198,0.1), inset 0 1px 3px rgba(0,0,0,0.3)'; }}
                  onBlur={(e) => { e.currentTarget.style.border = errors.identifier ? '1px solid rgba(239,68,68,0.5)' : '1px solid rgba(64,136,198,0.25)'; e.currentTarget.style.boxShadow = 'inset 0 1px 3px rgba(0,0,0,0.3)'; }}
                />
              ) : (
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => { setPhone(e.target.value); setErrors({}); }}
                  placeholder="+256 700 000 000"
                  className="w-full rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none transition-all duration-200"
                  style={{
                    background: errors.identifier ? 'rgba(239,68,68,0.1)' : 'rgba(10,22,40,0.7)',
                    border: errors.identifier ? '1px solid rgba(239,68,68,0.5)' : '1px solid rgba(64,136,198,0.25)',
                    boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.3)',
                  }}
                  onFocus={(e) => { e.currentTarget.style.border = '1px solid rgba(64,136,198,0.6)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(64,136,198,0.1), inset 0 1px 3px rgba(0,0,0,0.3)'; }}
                  onBlur={(e) => { e.currentTarget.style.border = errors.identifier ? '1px solid rgba(239,68,68,0.5)' : '1px solid rgba(64,136,198,0.25)'; e.currentTarget.style.boxShadow = 'inset 0 1px 3px rgba(0,0,0,0.3)'; }}
                />
              )}
              {errors.identifier && <p className="text-xs mt-1" style={{ color: '#F87171' }}>{errors.identifier}</p>}
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm font-semibold" style={{ color: '#CBD5E1' }}>Password</label>
                <a href="#" className="text-xs font-medium transition-colors" style={{ color: '#F5A623' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#F7BC55')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#F5A623')}
                >
                  Forgot password?
                </a>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setErrors({}); }}
                  placeholder="••••••••"
                  className="w-full rounded-xl px-4 py-3 pr-11 text-sm text-white placeholder-slate-500 focus:outline-none transition-all duration-200"
                  style={{
                    background: errors.password ? 'rgba(239,68,68,0.1)' : 'rgba(10,22,40,0.7)',
                    border: errors.password ? '1px solid rgba(239,68,68,0.5)' : '1px solid rgba(64,136,198,0.25)',
                    boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.3)',
                  }}
                  onFocus={(e) => { e.currentTarget.style.border = '1px solid rgba(64,136,198,0.6)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(64,136,198,0.1), inset 0 1px 3px rgba(0,0,0,0.3)'; }}
                  onBlur={(e) => { e.currentTarget.style.border = errors.password ? '1px solid rgba(239,68,68,0.5)' : '1px solid rgba(64,136,198,0.25)'; e.currentTarget.style.boxShadow = 'inset 0 1px 3px rgba(0,0,0,0.3)'; }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                  style={{ color: '#475569' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#94A3B8')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#475569')}
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {errors.password && <p className="text-xs mt-1" style={{ color: '#F87171' }}>{errors.password}</p>}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full font-bold py-3.5 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 mt-2 relative overflow-hidden"
              style={{
                background: loading ? 'rgba(64,136,198,0.5)' : 'linear-gradient(135deg, #4088C6, #5BA3D9)',
                color: '#fff',
                boxShadow: loading ? 'none' : '0 8px 24px rgba(64,136,198,0.4)',
                transform: loading ? 'none' : undefined,
              }}
              onMouseEnter={(e) => { if (!loading) { e.currentTarget.style.boxShadow = '0 12px 32px rgba(64,136,198,0.6)'; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = loading ? 'none' : '0 8px 24px rgba(64,136,198,0.4)'; e.currentTarget.style.transform = 'none'; }}
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Signing in…
                </>
              ) : (
                `Sign in as ${role === 'guest' ? 'Guest' : 'Host'}`
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px" style={{ background: 'rgba(64,136,198,0.2)' }} />
            <span className="text-xs font-medium" style={{ color: '#475569' }}>or continue with</span>
            <div className="flex-1 h-px" style={{ background: 'rgba(64,136,198,0.2)' }} />
          </div>

          {/* Social buttons */}
          <div className="grid grid-cols-2 gap-3">
            {[
              {
                label: 'Google',
                icon: (
                  <svg className="w-4 h-4" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                ),
              },
              {
                label: 'Facebook',
                icon: (
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#1877F2">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                ),
              },
            ].map(({ label, icon }) => (
              <button
                key={label}
                type="button"
                className="flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-medium transition-all duration-200"
                style={{ background: 'rgba(10,22,40,0.6)', border: '1px solid rgba(64,136,198,0.2)', color: '#CBD5E1' }}
                onMouseEnter={(e) => { e.currentTarget.style.border = '1px solid rgba(64,136,198,0.45)'; e.currentTarget.style.background = 'rgba(64,136,198,0.08)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.border = '1px solid rgba(64,136,198,0.2)'; e.currentTarget.style.background = 'rgba(10,22,40,0.6)'; }}
              >
                {icon}
                {label}
              </button>
            ))}
          </div>

          {/* Sign up link */}
          <p className="text-center text-sm mt-6" style={{ color: '#94A3B8' }}>
            Don&apos;t have an account?{' '}
            <a
              href="/signup"
              className="font-semibold transition-colors"
              style={{ color: '#F5A623' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#F7BC55')}
              onMouseLeave={(e) => (e.currentTarget.style.color = '#F5A623')}
            >
              Sign up
            </a>
          </p>
        </div>

        {/* Back link */}
        <p className="text-center text-sm mt-6" style={{ color: '#475569' }}>
          <a
            href="/home"
            className="transition-colors"
            onMouseEnter={(e) => (e.currentTarget.style.color = '#94A3B8')}
            onMouseLeave={(e) => (e.currentTarget.style.color = '#475569')}
          >
            ← Back to home
          </a>
        </p>
      </div>
    </div>
  );
}
